import { useState, useEffect, useMemo } from 'react';
import { BlobProvider } from '@react-pdf/renderer';
import { Document, Page, pdfjs } from 'react-pdf';
import BasicAtsTemplate from '../templates/BasicAtsTemplate';
import ErrorBoundary from './ErrorBoundary';
import 'react-pdf/dist/esm/Page/TextLayer.css';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import { Buffer } from 'buffer';
window.Buffer = Buffer;

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const InnerPDFContent = ({
  blob,
  error,
  loading,
  setBlobObject,
  setGenerationError,
  setGenerating,
  generationError,
  generating,
  cvData,
  containerWidth,
  numPages,
  setNumPages,
  pdfUrl,
  pdfOptions,
}) => {
  // Update parent state when blob, error, or loading changes
  useEffect(() => {
    setBlobObject(blob);
    setGenerationError(error);
    setGenerating(loading);
  }, [blob, error, loading, setBlobObject, setGenerationError, setGenerating]);

  const file = useMemo(() => (pdfUrl ? { url: pdfUrl } : null), [pdfUrl]);

  return (
    <div className="flex flex-col h-screen">
      {generating && (
        <div className="flex flex-col items-center justify-center h-full p-4">
          <div className="animate-pulse space-y-2 text-center">
            <div className="text-lg font-medium text-gray-700">
              Generating PDF Document...
            </div>
          </div>
        </div>
      )}

      {generationError && (
        <div className="p-4 text-center bg-red-50 rounded-lg">
          <p className="font-medium text-red-600">Failed to generate PDF</p>
        </div>
      )}

      {pdfUrl && (
        <div className="flex flex-col h-screen">
          <div className="flex items-center justify-between p-2">
            <div className="text-sm text-gray-600">
              {numPages && `Total pages: ${numPages}`}
            </div>
            <a
              href={pdfUrl}
              download={`${cvData.profileData?.fullname || 'cv'}.pdf`}
              className="px-4 py-2 text-white bg-blue-500 rounded hover:bg-blue-600 transition-colors"
            >
              Download PDF
            </a>
          </div>
          <div className="flex-1 overflow-y-auto bg-white border border-gray-200 rounded-lg">
          <Document
            file={file}
            onLoadSuccess={({ numPages }) => setNumPages(numPages)}
            options={pdfOptions}
            loading={
              <div className="flex items-center justify-center h-full">
                Loading PDF...
              </div>
            }
          >
            {Array.from(new Array(numPages), (_, index) => (
              <Page
                key={`page_${index + 1}`}
                pageNumber={index + 1}
                width={containerWidth}
                className="mx-auto my-4 shadow-sm"
              />
            ))}
          </Document>
          </div>
        </div>
      )}
    </div>
  );
};

export default function PDFViewerCustom({ cvData }) {
  const [numPages, setNumPages] = useState(null);
  const [containerWidth, setContainerWidth] = useState(800);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [blobObject, setBlobObject] = useState(null);
  const [generating, setGenerating] = useState(true);
  const [generationError, setGenerationError] = useState(null);

  const pdfOptions = useMemo(
    () => ({
      cMapUrl: `https://cdn.jsdelivr.net/npm/pdfjs-dist@${pdfjs.version}/cmaps/`,
      cMapPacked: true,
    }),
    [pdfjs.version]
  );

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      setContainerWidth(Math.min(window.innerWidth - 40, 800));
    };

    window.addEventListener('resize', handleResize);
    handleResize();

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Clean up blob URL
  useEffect(() => {
    return () => {
      if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl);
      }
    };
  }, [pdfUrl]);

  // Create object URL when blob changes
  // useEffect(() => {
  //   if (blobObject && !pdfUrl) {
  //     setPdfUrl(URL.createObjectURL(blobObject));
  //   }
  // }, [blobObject, pdfUrl]);


  useEffect(() => {
    if (blobObject) {
      const newUrl = URL.createObjectURL(blobObject);
      setPdfUrl(prev => {
        if (prev) URL.revokeObjectURL(prev);
        return newUrl;
      });
    }
    return () => {
      if (pdfUrl) URL.revokeObjectURL(pdfUrl);
    };
  }, [blobObject]);

  return (
    <ErrorBoundary>
      <BlobProvider document={<BasicAtsTemplate {...cvData} />}>
        {({ blob, url, loading, error }) => (
          <InnerPDFContent
            blob={blob}
            error={error}
            loading={loading}
            setBlobObject={setBlobObject}
            setGenerationError={setGenerationError}
            setGenerating={setGenerating}
            generationError={generationError}
            generating={generating}
            cvData={cvData}
            containerWidth={containerWidth}
            numPages={numPages}
            setNumPages={setNumPages}
            pdfUrl={pdfUrl}
            pdfOptions={pdfOptions}
          />
        )}
      </BlobProvider>
    </ErrorBoundary>
  );
}